import { useEffect, useRef, useState } from "react";
import { captureError } from "@/lib/sentry";
import AppLayout from "@/components/AppLayout";
import LockedFeature from "@/components/LockedFeature";
import { useAuth } from "@/context/AuthContext";
import { hasPlan, openUpgrade } from "@/lib/plans";
import { api, BACKEND_URL } from "@/lib/api";
import { PlusIcon, TrashIcon, ImageIcon, CardsThreeIcon } from "@phosphor-icons/react";

const DEFAULT_FORM = {
  player_name: "",
  card_name: "",
  year: new Date().getFullYear(),
  brand: "",
  serial_number: "",
  purchase_price: 0,
  estimated_value: 0,
  notes: "",
  image_path: null,
};

export default function Cards() {
  const { user } = useAuth();
  const isPro = hasPlan(user, "pro");
  const [cards, setCards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    const r = await api.get("/cards");
    setCards(r.data || []);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/cards/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: res.data.path }));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/cards", {
        ...form,
        year: Number(form.year),
        purchase_price: Number(form.purchase_price),
        estimated_value: Number(form.estimated_value),
      });
      setForm(DEFAULT_FORM);
      setOpen(false);
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this card?")) return;
    await api.delete(`/cards/${id}`);
    await refresh();
  };

  const totalValue = cards.reduce((s, c) => s + (c.estimated_value || 0), 0);
  const totalPurchase = cards.reduce((s, c) => s + (c.purchase_price || 0), 0);
  const pnl = totalValue - totalPurchase;
  const pnlPct = totalPurchase ? ((pnl / totalPurchase) * 100).toFixed(1) : "0.0";

  return (
    <AppLayout>
      <div className="mb-6 flex items-end justify-between gap-3 fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#32D74B] mb-2">Collection</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Sports Card Watchlist</h1>
        </div>
        <button
          data-testid="add-card-btn"
          onClick={() => (isPro ? setOpen(true) : openUpgrade({ code: "upgrade_required", required_plan: "pro", message: "The Sports Card Watchlist is part of Pro Fan." }))}
          className="px-4 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
        >
          <PlusIcon weight="bold" /> Add Card
        </button>
      </div>
      {!isPro && (
        <div className="mb-6">
          <LockedFeature testId="cards-locked" title="Sports Card Watchlist" desc="Track purchase price vs estimated value for your whole collection with Pro Fan." />
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Cards" value={cards.length} testId="stat-count" />
        <Stat label="Est Value" value={`$${totalValue.toLocaleString()}`} testId="stat-value" />
        <Stat label="P/L" value={`${pnl >= 0 ? "+" : ""}$${pnl.toLocaleString()} · ${pnlPct}%`} color={pnl >= 0 ? "#32D74B" : "#FF453A"} testId="stat-pnl" />
      </div>

      {cards.length === 0 ? (
        <div className="p-10 rounded-md bg-[#121212] border border-white/10 text-center">
          <CardsThreeIcon weight="bold" size={32} className="text-[#0A84FF] mx-auto mb-3" />
          <p className="text-white/70 font-medium mb-1">No cards yet</p>
          <p className="text-white/50 text-sm">Add your first card to start tracking value over time.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => {
            const delta = (c.estimated_value || 0) - (c.purchase_price || 0);
            const deltaColor = delta >= 0 ? "text-[#32D74B]" : "text-[#FF453A]";
            return (
              <div key={c.id} className="rounded-md bg-[#121212] border border-white/10 overflow-hidden" data-testid={`card-${c.id}`}>
                {c.image_path ? (
                  <CardImage path={c.image_path} alt={c.player_name} />
                ) : (
                  <div className="h-44 flex items-center justify-center bg-black/40 border-b border-white/5">
                    <ImageIcon weight="bold" size={32} className="text-white/20" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{c.year} · {c.brand}</p>
                    <button onClick={() => remove(c.id)} data-testid={`card-delete-${c.id}`} className="text-white/40 hover:text-[#FF453A]">
                      <TrashIcon size={14} />
                    </button>
                  </div>
                  <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-1">{c.player_name}</h3>
                  <p className="text-white/60 text-xs mb-3">{c.card_name || "—"} {c.serial_number ? `· #${c.serial_number}` : ""}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded bg-black/40 border border-white/5 px-2 py-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Paid</p>
                      <p className="font-num text-lg font-black">${(c.purchase_price || 0).toLocaleString()}</p>
                    </div>
                    <div className="rounded bg-black/40 border border-white/5 px-2 py-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Est</p>
                      <p className={`font-num text-lg font-black ${deltaColor}`}>${(c.estimated_value || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  {c.notes && <p className="mt-3 text-xs text-white/55">{c.notes}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-md bg-[#121212] border border-white/10 p-6 space-y-3 max-h-[90vh] overflow-y-auto"
            data-testid="add-card-modal"
          >
            <h2 className="font-display font-bold text-2xl uppercase tracking-tighter">Add Card</h2>
            <input data-testid="card-player" required placeholder="Player name *" value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            <input data-testid="card-name" placeholder="Card name (Optic Prizm RC)" value={form.card_name} onChange={(e) => setForm({ ...form, card_name: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="card-year" type="number" placeholder="Year *" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
              <input data-testid="card-brand" placeholder="Brand *" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            </div>
            <input data-testid="card-serial" placeholder="Serial number (12/99)" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="card-price" type="number" step="0.01" placeholder="Purchase price" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
              <input data-testid="card-value" type="number" step="0.01" placeholder="Estimated value" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            </div>
            <textarea data-testid="card-notes" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm min-h-[60px]" />

            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="card-file" />
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full py-2 rounded-md border border-dashed border-white/20 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white">
                {uploading ? "Uploading…" : form.image_path ? "Image uploaded ✓" : "Upload image (optional)"}
              </button>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-white/15 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white">Cancel</button>
              <button type="submit" disabled={saving} data-testid="card-submit" className="px-4 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-xs font-bold uppercase tracking-wider">
                {saving ? "Saving…" : "Save card"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AppLayout>
  );
}

function Stat({ label, value, color, testId }) {
  return (
    <div className="p-4 rounded-md bg-[#121212] border border-white/10" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-1">{label}</p>
      <p className="font-num text-2xl md:text-3xl font-black" style={color ? { color } : undefined}>{value}</p>
    </div>
  );
}

// Fetch protected image as blob and render it
function CardImage({ path, alt }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;
    api
      .get(`/files/${path}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setSrc(blobUrl);
      })
      .catch((err) => {
        if (!cancelled) {
          captureError(`card image load failed for ${path}:`, err);
          setSrc(null);
        }
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);
  if (!src) {
    return (
      <div className="h-44 flex items-center justify-center bg-black/40 border-b border-white/5">
        <ImageIcon weight="bold" size={32} className="text-white/20" />
      </div>
    );
  }
  return <img src={src} alt={alt} className="w-full h-44 object-cover border-b border-white/5" />;
}
